<app-header-nav></app-header-nav>

<div class="container mx-auto p-6">
  <h1 class="text-3xl font-bold mb-6">Discover Bars</h1>

  <!-- Filter Bar -->
  <mat-card class="p-4 mb-6">
    <div class="grid grid-cols-1 md:grid-cols-4 gap-3">
      <mat-form-field appearance="outline">
        <mat-label>Bar Name</mat-label>
        <input matInput [(ngModel)]="searchName" placeholder="Search by name..." (keyup.enter)="onSearch()">
        <mat-icon matPrefix>search</mat-icon>
      </mat-form-field>

      <mat-form-field appearance="outline">
        <mat-label>City</mat-label>
        <input matInput [(ngModel)]="searchCity" placeholder="e.g. Accra" (keyup.enter)="onSearch()">
        <mat-icon matPrefix>location_city</mat-icon>
      </mat-form-field>

      <mat-form-field appearance="outline">
        <mat-label>Country</mat-label>
        <input matInput [(ngModel)]="searchCountry" placeholder="e.g. Ghana" (keyup.enter)="onSearch()">
        <mat-icon matPrefix>public</mat-icon>
      </mat-form-field>

      <mat-form-field appearance="outline">
        <mat-label>Sort By</mat-label>
        <mat-select [(ngModel)]="sortBy" (ngModelChange)="onSortChange()">
          <mat-option value="name">Name (A-Z)</mat-option>
          <mat-option value="rating">Highest Rated</mat-option>
          <mat-option value="city">City</mat-option>
        </mat-select>
      </mat-form-field>
    </div>

    <div class="flex gap-3 mt-3 flex-wrap">
      <button mat-raised-button color="primary" (click)="onSearch()">
        <mat-icon>search</mat-icon> Search
      </button>
      <button mat-stroked-button (click)="clearFilters()">
        <mat-icon>clear</mat-icon> Clear
      </button>
      <div class="flex items-center gap-2">
        <button mat-stroked-button color="accent" (click)="findNearbyBars()" [disabled]="isLocating">
          <mat-spinner *ngIf="isLocating" diameter="18" class="inline-block mr-1"></mat-spinner>
          <mat-icon *ngIf="!isLocating">my_location</mat-icon>
          Bars Near Me
        </button>
        <mat-form-field appearance="outline" style="width:100px">
          <mat-label>Radius (km)</mat-label>
          <input matInput type="number" [(ngModel)]="radiusKm"
                 min="1" [max]="MAX_RADIUS_KM" (change)="onRadiusChange()">
        </mat-form-field>
      </div>
    </div>
  </mat-card>

  <!-- Results count -->
  <div class="text-gray-500 mb-4">
    <span *ngIf="!isLoading">{{ filteredBars.length }} bar{{ filteredBars.length !== 1 ? 's' : '' }} found</span>
    <span *ngIf="sortBy === 'rating'" class="ml-2 text-yellow-600 font-medium">★ Sorted by rating</span>
  </div>

  <!-- Loading -->
  <div *ngIf="isLoading" class="flex justify-center py-16"><mat-spinner></mat-spinner></div>

  <!-- Bar Grid -->
  <div *ngIf="!isLoading" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
    <mat-card *ngFor="let bar of filteredBars" class="cursor-pointer hover:shadow-lg transition-shadow">
      <!-- Cover image placeholder -->
      <div class="h-40 bg-gradient-to-br from-amber-700 to-amber-900 flex items-center justify-center rounded-t">
        <mat-icon class="text-white" style="font-size:48px">local_bar</mat-icon>
      </div>

      <div class="p-4">
        <div class="flex justify-between items-start">
          <h2 class="text-lg font-bold">{{ bar.name }}</h2>
          <div class="flex items-center gap-1 text-yellow-500">
            <mat-icon class="text-sm">star</mat-icon>
            <span class="font-semibold">{{ bar.averageRating | number:'1.1-1' }}</span>
            <span class="text-gray-400 text-xs">({{ bar.totalRatings }})</span>
          </div>
        </div>

        <div class="flex items-center text-gray-500 text-sm mt-1">
          <mat-icon class="text-sm mr-1">location_on</mat-icon>
          {{ bar.city }}<span *ngIf="bar.country">, {{ bar.country }}</span>
        </div>

        <p class="text-gray-600 text-sm mt-2 line-clamp-2">{{ bar.description || 'No description available' }}</p>

        <!-- Star display -->
        <div class="flex gap-0.5 mt-2">
          <mat-icon *ngFor="let star of getStarArray(bar.averageRating)" class="text-sm"
            [class.text-yellow-400]="isStarFilled(star, bar.averageRating)"
            [class.text-gray-300]="!isStarFilled(star, bar.averageRating)">star</mat-icon>
        </div>

        <div class="flex gap-2 mt-4">
          <button mat-raised-button color="primary" class="flex-1" (click)="visitBar(bar)">
            <mat-icon>local_bar</mat-icon> Visit
          </button>
          <button mat-stroked-button (click)="selectBar(bar)">
            <mat-icon>info</mat-icon>
          </button>
          <button mat-stroked-button color="accent" (click)="openRatingDialog(bar)">
            <mat-icon>star_rate</mat-icon>
          </button>
        </div>
      </div>
    </mat-card>
  </div>

  <!-- Say WHICH of the two situations this is. A single "no bars found"
       covered both "nothing within reach" and "we never asked where you are",
       and it also used to be shown when the request had failed outright — a
       broken platform reading as an empty one. -->
  <p *ngIf="!isLoading && !isLocating && locationUnavailable && filteredBars.length === 0"
     class="text-center text-gray-500 py-16">
    <mat-icon style="font-size:64px" class="text-gray-300">location_off</mat-icon>
    <br>We do not know where you are, so we cannot show any bars.
    <br>Bars more than 100km away are never listed, so location access is required.
  </p>

  <p *ngIf="!isLoading && !isLocating && !locationUnavailable && filteredBars.length === 0"
     class="text-center text-gray-500 py-16">
    <mat-icon style="font-size:64px" class="text-gray-300">search_off</mat-icon>
    <br>No bars within {{ radiusKm }}km{{ searchName || searchCity || searchCountry ? ' match those filters' : '' }}.
    <br>Try widening the radius or clearing the filters.
  </p>

  <p *ngIf="isLocating" class="text-center text-gray-500 py-16">
    <mat-icon style="font-size:64px" class="text-gray-300">my_location</mat-icon>
    <br>Finding bars near you…
  </p>
</div>

<!-- Bar Detail Side Panel -->
<div *ngIf="selectedBar" class="fixed inset-0 bg-black bg-opacity-50 flex justify-end z-50" (click)="closeDetail()">
  <div class="bg-white w-full max-w-md h-full overflow-y-auto p-6" (click)="$event.stopPropagation()">
    <div class="flex justify-between items-center mb-6">
      <h2 class="text-xl font-bold">{{ selectedBar.name }}</h2>
      <button mat-icon-button (click)="closeDetail()"><mat-icon>close</mat-icon></button>
    </div>

    <div class="flex items-center gap-2 mb-4">
      <mat-icon class="text-yellow-500">star</mat-icon>
      <span class="text-xl font-bold">{{ selectedBar.averageRating | number:'1.1-1' }}</span>
      <span class="text-gray-500">({{ selectedBar.totalRatings }} ratings)</span>
    </div>

    <div class="space-y-2 mb-6 text-sm">
      <div class="flex gap-2"><mat-icon class="text-gray-400">location_on</mat-icon>
        <span>{{ selectedBar.addressLine1 }}, {{ selectedBar.city }}, {{ selectedBar.country }}</span>
      </div>
      <div class="flex gap-2" *ngIf="selectedBar.phone"><mat-icon class="text-gray-400">phone</mat-icon><span>{{ selectedBar.phone }}</span></div>
      <div class="flex gap-2" *ngIf="selectedBar.website"><mat-icon class="text-gray-400">language</mat-icon><span>{{ selectedBar.website }}</span></div>
    </div>

    <p class="text-gray-600 mb-6">{{ selectedBar.description }}</p>

    <h3 class="font-bold mb-3">Most Popular Drinks</h3>
    <div *ngIf="loadingDrinks" class="flex justify-center py-4"><mat-spinner diameter="32"></mat-spinner></div>
    <div *ngIf="!loadingDrinks">
      <div *ngFor="let drink of popularDrinks; let i = index" class="flex items-center gap-3 py-2 border-b">
        <span class="w-6 h-6 bg-amber-500 text-white rounded-full flex items-center justify-center text-xs font-bold">{{ i + 1 }}</span>
        <div class="flex-1">
          <div class="font-medium">{{ drink.drinkName }}</div>
          <div class="text-xs text-gray-500">{{ drink.totalOrders }} orders</div>
        </div>
        <div class="text-green-600 font-medium">{{ drink.totalRevenue | currency }}</div>
      </div>
      <p *ngIf="popularDrinks.length === 0" class="text-gray-400 text-sm">No data available</p>
    </div>

    <div class="flex gap-2 mt-6">
      <button mat-raised-button color="primary" class="flex-1" (click)="visitBar(selectedBar)">
        <mat-icon>local_bar</mat-icon> Order Now
      </button>
      <button mat-stroked-button color="accent" (click)="openRatingDialog(selectedBar)">
        <mat-icon>star_rate</mat-icon> Rate
      </button>
    </div>
  </div>
</div>

<!-- Rating Dialog -->
<div *ngIf="showRatingDialog" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
  <mat-card class="p-6 w-80">
    <h3 class="text-lg font-bold mb-4">Rate this Bar</h3>
    <div class="flex gap-1 justify-center mb-4">
      <button *ngFor="let star of ratingStars" mat-icon-button
        (click)="ratingValue = star"
        [class.text-yellow-400]="star <= ratingValue">
        <mat-icon style="font-size:32px">{{ star <= ratingValue ? 'star' : 'star_border' }}</mat-icon>
      </button>
    </div>
    <div class="text-center text-gray-500 mb-4">{{ ratingValue }} / 5 stars</div>
    <div class="flex gap-2">
      <button mat-button class="flex-1" (click)="closeRatingDialog()">Cancel</button>
      <button mat-raised-button color="primary" class="flex-1" (click)="submitRating()">Submit</button>
    </div>
  </mat-card>
</div>

<app-footer></app-footer>
